import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/apiClient";
import { useSettings } from "@/hooks/useSettings";
import { useFilter } from "@/context/FilterContext";
import { computeRange } from "@/lib/dateRange";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, ExternalLink, Search } from "lucide-react";
import { toast } from "sonner";

const STATUS_COLORS = {
  Published: "rag-green", Approved: "rag-green", Won: "rag-green", Onboarded: "rag-green", Delivered: "rag-green", Positive: "rag-green", Active: "rag-green", Y: "rag-green",
  Confirmed: "rag-amber", Review: "rag-amber", Shortlisted: "rag-amber", Negotiating: "rag-amber", Pending: "rag-amber", Editing: "rag-amber", Draft: "rag-amber", Neutral: "rag-amber", Applied: "rag-amber",
  Pitched: "rag-red", "Not Won": "rag-red", Inactive: "rag-red", Late: "rag-red", Negative: "rag-red", N: "rag-red",
};

function resolveOptions(col, settings) {
  if (Array.isArray(col.options)) return col.options;
  if (typeof col.options === "string" && col.options.startsWith("$")) {
    return settings?.[col.options.slice(1)] || [];
  }
  return [];
}

export default function DataTable({ title, endpoint, columns, extraColumns = [], globalFilter = true, defaults = {}, rowFilter = null }) {
  const qc = useQueryClient();
  const { data: settings } = useSettings();
  const { range, employee } = useFilter();
  const { data: teamRows = [] } = useQuery({ queryKey: ["team"], queryFn: async () => (await api.get("/team")).data });
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [search, setSearch] = useState("");

  const { data: rows = [], isLoading } = useQuery({
    queryKey: [endpoint],
    queryFn: async () => (await api.get(`/${endpoint}`)).data,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: [endpoint] });
    qc.invalidateQueries({ queryKey: ["dashboard"] });
    qc.invalidateQueries({ queryKey: ["insights"] });
    qc.invalidateQueries({ queryKey: ["kpi-summary"] });
    qc.invalidateQueries({ queryKey: ["manager-dashboard"] });
  };

  const saveMut = useMutation({
    mutationFn: async (payload) =>
      editing ? api.put(`/${endpoint}/${editing.id}`, payload) : api.post(`/${endpoint}`, payload),
    onSuccess: () => { invalidate(); setOpen(false); toast.success(editing ? "Updated" : "Added"); },
    onError: () => toast.error("Save failed"),
  });

  const delMut = useMutation({
    mutationFn: async (id) => api.delete(`/${endpoint}/${id}`),
    onSuccess: () => { invalidate(); toast.success("Deleted"); },
  });

  const openAdd = () => { setEditing(null); setForm({}); setOpen(true); };
  const openEdit = (row) => { setEditing(row); setForm({ ...row }); setOpen(true); };

  const submit = () => {
    const req = columns[0];
    if (!form[req.key] && form[req.key] !== 0) {
      toast.error(`${req.label} is required`);
      return;
    }
    const payload = {};
    columns.forEach((c) => {
      let v = form[c.key];
      if (c.type === "number" && v !== undefined && v !== "") v = Number(v);
      payload[c.key] = v ?? "";
    });
    saveMut.mutate({ ...payload, ...defaults });
  };

  const optionsFor = (c) =>
    c.type === "employee"
      ? teamRows.filter((t) => (!c.role || t.role === c.role) && t.status !== "Inactive").map((t) => t.name)
      : resolveOptions(c, settings);

  const r = globalFilter && range ? computeRange(range) : {};
  const sourceRows = rowFilter ? rows.filter(rowFilter) : rows;
  const filtered = sourceRows.filter((row) => {
    if (search && !JSON.stringify(row).toLowerCase().includes(search.toLowerCase())) return false;
    if (globalFilter && (r.start || r.end)) {
      const d = row.date || row.created_at;
      if (d) {
        const t = new Date(d).toISOString().slice(0, 19);
        if (r.start && t < r.start) return false;
        if (r.end && t > r.end) return false;
      }
    }
    if (globalFilter && employee) {
      const vals = Object.values(row).map((v) => String(v));
      if (!vals.includes(employee)) return false;
    }
    return true;
  });

  return (
    <div className="rounded-lg border border-surface-hover bg-surface" data-testid={`table-${endpoint}`}>
      <div className="flex items-center justify-between p-5 border-b border-surface-hover">
        <div>
          <h3 className="font-heading text-lg font-bold">{title}</h3>
          <p className="text-xs text-slatetext mt-0.5">{filtered.length} record(s)</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="w-4 h-4 text-slatetext absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              data-testid={`search-${endpoint}`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search…"
              className="bg-navy border border-surface-hover rounded-md pl-9 pr-3 py-2 text-sm text-white focus:border-gold outline-none w-48"
            />
          </div>
          <Button data-testid={`add-${endpoint}`} onClick={openAdd} className="bg-gold text-navy hover:bg-gold-hover gap-2">
            <Plus className="w-4 h-4" /> Add
          </Button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-slatetext text-xs uppercase tracking-wide border-b border-surface-hover">
              {columns.map((c) => (
                <th key={c.key} className={`px-4 py-3 font-medium whitespace-nowrap ${c.type === "number" ? "text-right" : "text-left"}`}>
                  {c.label}
                </th>
              ))}
              {extraColumns.map((c) => <th key={c.key} className="px-4 py-3 text-left">{c.label}</th>)}
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={columns.length + 2} className="px-4 py-10 text-center text-slatetext">Loading…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={columns.length + 2} className="px-4 py-10 text-center text-slatetext">No records yet. Click Add to create one.</td></tr>
            ) : filtered.map((row) => (
              <tr key={row.id} className="border-b border-surface-hover/60 hover:bg-surface-hover/40 transition-colors duration-200">
                {columns.map((c) => (
                  <td key={c.key} className={`px-4 py-3 whitespace-nowrap ${c.type === "number" ? "text-right tabular-nums" : "text-left"}`}>
                    {renderCell(c, row[c.key])}
                  </td>
                ))}
                {extraColumns.map((c) => <td key={c.key} className="px-4 py-3">{c.render(row)}</td>)}
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button data-testid={`edit-${endpoint}-${row.id}`} onClick={() => openEdit(row)} className="text-slatetext hover:text-gold transition-colors duration-200 mr-3">
                    <Pencil className="w-4 h-4 inline" />
                  </button>
                  <button data-testid={`delete-${endpoint}-${row.id}`} onClick={() => { if (window.confirm("Delete this record?")) delMut.mutate(row.id); }} className="text-slatetext hover:text-[#DC2626] transition-colors duration-200">
                    <Trash2 className="w-4 h-4 inline" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-surface border-surface-hover text-white max-w-2xl max-h-[85vh] overflow-y-auto" data-testid={`dialog-${endpoint}`}>
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Edit" : "Add"} {title}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            {columns.map((c) => (
              <div key={c.key} className={c.type === "text" && c.key.includes("note") ? "col-span-2" : ""}>
                <Label className="text-slatetext text-xs mb-1.5 block">{c.label}</Label>
                {c.type === "select" || c.type === "employee" ? (
                  <select
                    data-testid={`field-${c.key}`}
                    value={form[c.key] || ""}
                    onChange={(e) => setForm({ ...form, [c.key]: e.target.value })}
                    className="w-full bg-navy border border-surface-hover rounded-md px-3 py-2 text-sm text-white focus:border-gold outline-none"
                  >
                    <option value="">Select…</option>
                    {optionsFor(c).map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : (
                  <Input
                    data-testid={`field-${c.key}`}
                    type={c.type === "number" ? "number" : c.type === "date" ? "date" : "text"}
                    value={form[c.key] ?? ""}
                    onChange={(e) => setForm({ ...form, [c.key]: e.target.value })}
                    className="bg-navy border-surface-hover text-white focus:border-gold"
                  />
                )}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} className="text-slatetext">Cancel</Button>
            <Button data-testid={`save-${endpoint}`} onClick={submit} disabled={saveMut.isPending} className="bg-gold text-navy hover:bg-gold-hover">
              {saveMut.isPending ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function renderCell(col, value) {
  if (value === undefined || value === null || value === "") return <span className="text-slatetext">—</span>;
  if (col.type === "link") {
    return <a href={value} target="_blank" rel="noreferrer" className="text-gold hover:underline inline-flex items-center gap-1">Link <ExternalLink className="w-3 h-3" /></a>;
  }
  if (col.type === "number") {
    return <span className="tabular-nums">{Number(value).toLocaleString("en-IN")}</span>;
  }
  if (col.type === "select" && STATUS_COLORS[value]) {
    return <span className={`px-2 py-0.5 rounded text-xs font-medium ${STATUS_COLORS[value]}`}>{value}</span>;
  }
  return <span>{String(value)}</span>;
}
